import React, { useState, useEffect } from 'react';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import TokenService from '../../api/tokenService';
import EdizioneService from '../../api/edizioneService';
import { useMe } from '../../hooks/useMe';
import './sidebar.css';

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [selectedEdition, setSelectedEdition] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const username = TokenService.getUsername() || 'Utente';
  const { me, daDecidere } = useMe();

  // La voce Utenti compare solo a chi amministra utenti o gruppi in almeno un ambito. Le altre
  // voci restano come sono: le risorse operative non sono ancora sotto controllo dei permessi, e
  // nasconderle adesso toglierebbe il menu a chi il backfill non ha coperto.
  const amministraUtenti = !!me && (
    me.bypass
    || ['UTENTE', 'GRUPPO_UTENTI'].some(r => me.globale?.[r] && me.globale[r] !== 'NEGATO')
    || [...(me.gruppiTornei || []), ...(me.edizioni || [])].some(
         a => ['UTENTE', 'GRUPPO_UTENTI'].some(r => a.permessi?.[r] && a.permessi[r] !== 'NEGATO'))
  );

  useEffect(() => {
    setSelectedEdition(EdizioneService.get());

    const handleEditionChange = (e) => {
      const newEdition = e.detail.edition;
      setSelectedEdition(newEdition);
    };

    window.addEventListener(EdizioneService.evento, handleEditionChange);

    return () => {
      window.removeEventListener(EdizioneService.evento, handleEditionChange);
    };
  }, []);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = () => {
    TokenService.removeToken();
    EdizioneService.clear();
    navigate('/login');
  };

  const handleGoBack = () => {
    EdizioneService.clear();
    setSelectedEdition(null);
    navigate('/admin/tornei');
  };

  return (
    <div className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
      <div className="burger-button" onClick={toggleSidebar}>
        <div className="burger-icon">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      
      <div className="sidebar-header">
        <h3>ScoutballItalia</h3>
        <p className="user-name">{username}</p>
      </div>
      
      <ul className="sidebar-menu">
        {!selectedEdition ? (
          <>
            <li className="sidebar-item">
              <NavLink to="/admin/tornei" className={({isActive}) => isActive ? "active" : ""}>
                <div className="menu-icon">
                  <i className="fas fa-trophy"></i>
                </div>
                <span className="menu-text">Tornei</span>
              </NavLink>
            </li>
            {amministraUtenti && (
              <li className="sidebar-item">
                <NavLink to="/admin/utenti" className={({isActive}) => isActive ? "active" : ""}>
                  <div className="menu-icon">
                    <i className="fas fa-user-shield"></i>
                  </div>
                  <span className="menu-text">Utenti</span>
                  {daDecidere > 0 && (
                    <span className="menu-badge" title={`${daDecidere} richieste in attesa di approvazione`}>
                      {daDecidere}
                    </span>
                  )}
                </NavLink>
              </li>
            )}
            <li className="sidebar-item">
              <NavLink to="/admin/gruppi" className={({isActive}) => isActive ? "active" : ""}>
                <div className="menu-icon">
                  <i className="fas fa-campground"></i>
                </div>
                <span className="menu-text">Gruppi Scout</span>
              </NavLink>
            </li>
            <li className="sidebar-item">
              <NavLink to="/admin/squadre-storiche" className={({isActive}) => isActive ? "active" : ""}>
                <div className="menu-icon">
                  <i className="fas fa-clock-rotate-left"></i>
                </div>
                <span className="menu-text">Squadre storiche</span>
              </NavLink>
            </li>
          </>
        ) : (
          <>
            <li className="sidebar-item">
              <NavLink to="/admin/accettazione" className={({isActive}) => isActive ? "active" : ""}>
                <div className="menu-icon">
                  <i className="fas fa-clipboard-check"></i>
                </div>
                <span className="menu-text">Accettazione</span>
              </NavLink>
            </li>
            <li className="sidebar-item">
              <NavLink to="/admin/formula" className={({isActive}) => isActive ? "active" : ""}>
                <div className="menu-icon">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <span className="menu-text">Formula</span>
              </NavLink>
            </li>
            <li className="sidebar-item">
              <NavLink to="/admin/planning" className={({isActive}) => isActive ? "active" : ""}>
                <div className="menu-icon">
                  <i className="fas fa-tasks"></i>
                </div>
                <span className="menu-text">Planning</span>
              </NavLink>
            </li>
            
            <li className="sidebar-item">
              <NavLink to="/admin/partite" className={({isActive}) => isActive ? "active" : ""}>
                <div className="menu-icon">
                  <i className="fas fa-futbol"></i>
                </div>
                <span className="menu-text">Partite</span>
              </NavLink>
            </li>
            <li className="sidebar-item">
              <NavLink to="/admin/risultati" className={({isActive}) => isActive ? "active" : ""}>
                <div className="menu-icon">
                  <i className="fas fa-poll"></i>
                </div>
                <span className="menu-text">Risultati</span>
              </NavLink>
            </li>
            <li className="sidebar-item">
              <NavLink to="/admin/classifiche" className={({isActive}) => isActive ? "active" : ""}>
                <div className="menu-icon">
                  <i className="fas fa-list-ol"></i>
                </div>
                <span className="menu-text">Classifiche</span>
              </NavLink>
            </li>
            <li className="sidebar-item">
              <NavLink to="/admin/statistiche" className={({isActive}) => isActive ? "active" : ""}>
                <div className="menu-icon">
                  <i className="fas fa-chart-bar"></i>
                </div>
                <span className="menu-text">Statistiche</span>
              </NavLink>
            </li>
            <li className="sidebar-item">
              <NavLink to="/admin/fantascoutball" className={({isActive}) => isActive ? "active" : ""}>
                <div className="menu-icon">
                  <i className="fas fa-star"></i>
                </div>
                <span className="menu-text">Fantascoutball</span>
              </NavLink>
            </li>
            <li className="sidebar-item">
              <NavLink to="/admin/foto" className={({isActive}) => isActive ? "active" : ""}>
                <div className="menu-icon">
                  <i className="fas fa-images"></i>
                </div>
                <span className="menu-text">Foto</span>
              </NavLink>
            </li>
            <li className="sidebar-item">
              <NavLink to="/admin/impostazioni" className={({isActive}) => isActive ? "active" : ""}>
                <div className="menu-icon">
                  <i className="fas fa-cog"></i>
                </div>
                <span className="menu-text">Impostazioni</span>
              </NavLink>
            </li>
          </>
        )}
      </ul>
      
      <div className="sidebar-footer">
        {selectedEdition ? (
          <button className="logout-button" onClick={handleGoBack}>
            <i className="fas fa-arrow-left"></i>
            <span className="logout-text">Indietro</span>
          </button>
        ) : (
          <button className="logout-button" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i>
            <span className="logout-text">Logout</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default Sidebar;